import { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { findEmployees, type Employee } from '../../../api/employees';
import { toUserMessage } from '../../../api/client';
import { formatFullName } from '../../../utils/formatFullName';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { Avatar } from '../Avatar';
import { Dropdown } from '../Dropdown';

const ITEMS_PER_PAGE = 20;
const DEBOUNCE_MS = 300;

const InputWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: 160px;
  height: 32px;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 0 28px 0 12px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.disabled};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms, color 150ms;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight.gray25};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const ListItem = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.highlight.blue25 : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary.main : theme.colors.text.primary};
  transition: background 150ms;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.highlight.blue25 : theme.colors.highlight.gray25};
  }
`;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadingMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

function getEmployeeDisplayName(emp: Employee): string {
  const fullName = formatFullName(emp.familyName, emp.givenName, emp.patronymic);
  return fullName || emp.email || emp.id;
}

export interface EmployeeFilterSelectProps {
  value?: Employee | null;
  onChange?: (employee: Employee | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

export function EmployeeFilterSelect({
  value,
  onChange,
  placeholder = 'Сотрудник',
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: EmployeeFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteGuardName = `sew-employee-combobox-${useId().replace(/:/g, '')}`;

  const debouncedSearch = useDebouncedValue(
    value && !editing ? '' : inputValue.trim(),
    DEBOUNCE_MS,
  );

  const loadItems = useCallback(async (search: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await findEmployees({
        search,
        page: 1,
        itemsPerPage: ITEMS_PER_PAGE,
      });
      const sorted = [...data.items].sort((a, b) => {
        const nameA = getEmployeeDisplayName(a).toLowerCase();
        const nameB = getEmployeeDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setItems(sorted);
    } catch (err) {
      setError(toUserMessage(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void loadItems(debouncedSearch);
    }
  }, [open, debouncedSearch, loadItems]);

  const handleFocus = useCallback(() => {
    if (value) {
      setEditing(true);
      setInputValue('');
    }
    setOpen(true);
  }, [value]);

  const handleSelect = useCallback(
    (emp: Employee) => {
      onChange?.(emp);
      setInputValue('');
      setEditing(false);
      setOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(null);
      setInputValue('');
      setEditing(false);
      setOpen(false);
      inputRef.current?.focus();
    },
    [onChange],
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setEditing(false);
      setInputValue('');
    }
    setOpen(nextOpen);
  }, []);

  const displayValue = value && !editing ? getEmployeeDisplayName(value) : inputValue;
  const isReadOnly = !!(value && !editing);

  const trigger = (
    <InputWrapper onClick={(e) => open && e.stopPropagation()}>
      <StyledInput
        ref={inputRef}
        type="text"
        name={autocompleteGuardName}
        autoComplete="off"
        value={displayValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={isReadOnly}
        id={id}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-autocomplete="list"
      />
      {value && (
        <ClearButton
          type="button"
          onClick={handleClear}
          aria-label="Очистить"
          tabIndex={-1}
        >
          ×
        </ClearButton>
      )}
    </InputWrapper>
  );

  return (
    <Dropdown
      trigger={trigger}
      open={open}
      onOpenChange={handleOpenChange}
      width="280px"
    >
      {loading && <LoadingMessage>Загрузка...</LoadingMessage>}
      {error && <EmptyMessage role="alert">{error}</EmptyMessage>}
      {!loading && !error && items.length === 0 && (
        <EmptyMessage>Нет сотрудников</EmptyMessage>
      )}
      {!loading && !error && items.length > 0 && (
        <List role="listbox">
          {items.map((emp) => (
            <ListItem
              key={emp.id}
              $selected={value?.id === emp.id}
              role="option"
              onClick={() => handleSelect(emp)}
            >
              <Avatar
                url={emp.avatarUrl}
                letter={getEmployeeDisplayName(emp)}
                size="xxs"
                alt={getEmployeeDisplayName(emp)}
              />
              <span>
                {getEmployeeDisplayName(emp)}
                {emp.email && (
                  <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
                    {' '}
                    — {emp.email}
                  </span>
                )}
              </span>
            </ListItem>
          ))}
        </List>
      )}
    </Dropdown>
  );
}
